import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'

import { TaxSeasonKickoffSurveyProps } from './YearEndKickoffSurvey'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import {
  Card,
  CardColors,
  Form,
  Icon,
  Image,
  List,
  RadioGroupItem,
  Text,
} from '@/ui'
import { TaxFormType } from '../questionnaires/constants'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectCurrentAnnualTaxDetails,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectCurrentAnnualTaxFiling,
  selectTskIsScorp,
  selectTskIsSprop,
} from '../annualTaxFilings.selector'
import { TAX_SEASON_KICKOFF_SURVEY } from './TskTipsSideBar'
import { convertUtcToLocalDate, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectPersonalFilingIncludedForFree } from '../tax-checklist/taxChecklist.selectors'
import { getFieldNames, makeReqStringSchema } from '@/base'
import { TypographyStyles } from '@/styles'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
const FIELD_NAMES = {
  form1040Selected: 'form1040Selected',
  form1120sSelected: 'form1120sSelected',
} as const

type FormFieldName = (typeof FIELD_NAMES)[keyof typeof FIELD_NAMES]

export const OptOutMessage = ({ fieldName }: { fieldName: FormFieldName }) => {
  const isSoleProp = useReselector(selectTskIsSprop)
  const annualTaxDetails = useReselector(selectCurrentAnnualTaxDetails)

  const form1040OptInCutoff = convertUtcToLocalDate(
    annualTaxDetails?.form_1040_tq_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)
  const form1120sOptInCutoff = convertUtcToLocalDate(
    annualTaxDetails?.form_1120_s_tq_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)
  const forcedExtensionDate = convertUtcToLocalDate(
    annualTaxDetails?.tax_entity_verification_due_date
  )?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)

  return (
    <Card color={CardColors.LEVEL_1}>
      <Text variant={TypographyStyles.BODY_SM}>
        {isSoleProp ? (
          <>
            If you change your mind, you can opt back in through the Annual Tax
            Center by {form1040OptInCutoff}.
          </>
        ) : (
          <>
            {fieldName === FIELD_NAMES.form1120sSelected && (
              <>
                Note: If you do not file your Business tax filing (Form 1120-S)
                with us, we will not be able to file your Personal tax filing
                (Form 1040) either.
                <br />
                <br />
                If you change your mind, you can opt back in through the Annual
                Tax Center by {form1120sOptInCutoff}.
              </>
            )}
            {fieldName === FIELD_NAMES.form1040Selected && (
              <>
                Note: Even though you have decided not to file your Personal tax
                filing (Form 1040) with us, keep in mind that you will still
                need to work with a different tax preparer to file this with the
                IRS.
                <br />
                <br />
                Opt back in through the Annual Tax Center by{' '}
                {form1120sOptInCutoff} before you complete your business tax
                questionnaire.
              </>
            )}
          </>
        )}
      </Text>
      <Text variant={TypographyStyles.BODY_SM} className="mt-3">
        If you opt back in after {forcedExtensionDate}, we may need to file an
        extension.
      </Text>
    </Card>
  )
}

const CheckListItem = ({
  title,
  description,
}: {
  title: string
  description: string
}) => (
  <List.Item
    customIcon={<Icon icon={faCheck} color="green" />}
    className="mb-4"
  >
    <div className="flex flex-col gap-1">
      <Text variant={TypographyStyles.H3}>{title}</Text>
      <Text variant={TypographyStyles.BODY_SM} className="mt-2">
        {description}
      </Text>
    </div>
  </List.Item>
)

const TaxNeeds = ({ goToNextStep, goBack }: TaxSeasonKickoffSurveyProps) => {
  const currentAnnualTaxFiling = useReselector(selectCurrentAnnualTaxFiling)
  const currentAnnualTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const isSCorporation = useReselector(selectTskIsScorp)
  const isSoleProp = useReselector(selectTskIsSprop)

  const hasFree2ndFilingPermission = useReselector(
    selectPersonalFilingIncludedForFree
  )

  const formik = useFormik({
    initialValues: {
      form1040Selected: undefined as 'true' | 'false' | undefined,
      form1120sSelected: undefined as 'true' | 'false' | undefined,
    },
    onSubmit: ({ form1040Selected, form1120sSelected }) => {
      const selectedForms = []
      const now = DateTime.now().toISO()

      if (isSoleProp) {
        if (form1040Selected === 'true') {
          selectedForms.push(TaxFormType.form1040)
        } else {
          goToNextStep(
            {
              annualTaxFormNeeds: null,
              optedOutAt: now,
            },
            TAX_SEASON_KICKOFF_SURVEY.review
          )
          return
        }
      } else {
        if (form1120sSelected === 'true') {
          selectedForms.push(TaxFormType.form1120s)
          if (
            form1040Selected === 'true' ||
            // This inclusion is on the next page so don't remove this value unless the user answered false
            (form1040Selected !== 'false' &&
              currentAnnualTaxFiling?.annualTaxFormNeeds?.includes(
                TaxFormType.form1040
              ))
          ) {
            selectedForms.push(TaxFormType.form1040)
          }
        } else {
          goToNextStep(
            {
              annualTaxFormNeeds: null,
              optedOutAt: now,
            },
            TAX_SEASON_KICKOFF_SURVEY.review
          )
          return
        }
      }

      goToNextStep(
        {
          annualTaxFormNeeds: selectedForms,
          optedOutAt: null,
        },
        TAX_SEASON_KICKOFF_SURVEY.taxConfirmation
      )
    },
  })

  const { submitForm, isSubmitting, values } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <div className="mt-10 flex justify-center">
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/calendar_month.svg"
          alt="calendar"
          width={180}
          height={180}
        />
      </div>

      <Text variant={TypographyStyles.H1} textAlign="center" className="mt-6">
        What to expect for {currentAnnualTaxYear} taxes
      </Text>

      <Text className="mt-6" variant={TypographyStyles.BODY_MD}>
        Let’s make sure you get the right level of support. Here’s what’s
        included with your subscription:
      </Text>

      <Card className="mt-6" color={CardColors.EMPHASIS}>
        <List>
          {isSCorporation && (
            <CheckListItem
              title="Business tax filing (Form 1120-S)"
              description="Federal income tax return for S corporations."
            />
          )}
          {(isSoleProp || hasFree2ndFilingPermission) && (
            <CheckListItem
              title="Personal tax filing (Form 1040)"
              description="Federal individual income tax return for you and your spouse (if applicable)."
            />
          )}
          <CheckListItem
            title="State tax filings"
            description="Multi-state tax return filings wherever you have filing requirements"
          />
          <CheckListItem
            title="1099-NEC (if needed)"
            description="Contractor tax payroll forms"
          />
          <CheckListItem
            title="Tax extensions (if needed)"
            description="If you need additional time to file and obtain financial records and tax documents, we can file a tax extension."
          />
        </List>
      </Card>

      <Text className="mt-10" variant={TypographyStyles.H2}>
        Your tax needs
      </Text>

      {isSoleProp && (
        <div className="mt-6">
          <Form.RadioGroup
            name={fieldNames.form1040Selected}
            label="Would you like Heard to handle your personal taxes this year?"
            schema={makeReqStringSchema()}
          >
            <RadioGroupItem
              label="Yes, file my personal taxes with Heard"
              value="true"
            />
            <RadioGroupItem
              label="No, I’ll take care of my personal taxes myself"
              value="false"
            />
            {values.form1040Selected === 'false' && (
              <OptOutMessage fieldName={FIELD_NAMES.form1040Selected} />
            )}
          </Form.RadioGroup>
        </div>
      )}
      {isSCorporation && (
        <>
          <div className="mt-6">
            <Form.RadioGroup
              name={fieldNames.form1120sSelected}
              label="Do you want to file your business taxes with Heard?"
              schema={makeReqStringSchema()}
            >
              <RadioGroupItem
                label="Yes, file my business taxes with Heard"
                value="true"
              />
              <RadioGroupItem
                label="No, I’ll take care of my business taxes myself"
                value="false"
              />
              {values.form1120sSelected === 'false' && (
                <OptOutMessage fieldName={FIELD_NAMES.form1120sSelected} />
              )}
            </Form.RadioGroup>
          </div>
          {values.form1120sSelected === 'true' &&
            hasFree2ndFilingPermission && (
              <div className="mt-10">
                <Form.RadioGroup
                  name={fieldNames.form1040Selected}
                  label="Would you like Heard to also handle your personal taxes this year?"
                  schema={makeReqStringSchema()}
                >
                  <RadioGroupItem
                    label="Yes, also file my personal taxes with Heard"
                    value="true"
                  />
                  <RadioGroupItem
                    label="No, I’ll take care of my personal taxes myself"
                    value="false"
                  />
                  {values.form1040Selected === 'false' && (
                    <OptOutMessage fieldName={FIELD_NAMES.form1040Selected} />
                  )}
                </Form.RadioGroup>
              </div>
            )}
        </>
      )}

      <FormFlowFooter
        onBack={() => goBack(TAX_SEASON_KICKOFF_SURVEY.contractorPayroll)}
        onForward={submitForm}
        continueDisabled={isSubmitting}
        loading={isSubmitting}
      />
    </FormikProvider>
  )
}

export default TaxNeeds
